import { optionsAttendeeStatus } from "@/pages/taskpane/features/resources/services/useGetAttendeeStatus";
import { options } from "@/pages/taskpane/features/resources/services/useGetSelectedResource";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  AttendeeCalendarEvent,
  AttendeeInfo,
  IAttendeeInfo,
  IGetAttendeeStatusRequest,
} from "../features/resources/services/types/attendeeEntity";
import { EDisableRule, EPermissionType } from "../features/resources/services/types/enum";
import {
  GetSelectedResourceResponse,
  IGetResourceBookingRequest,
  ResourceEntity,
} from "../features/resources/services/types/resourceEntity";
import { useOutlookActions } from "../lib/office/hooks";
import { mapPermissionMessage } from "../lib/office/utils";
import { generateCId } from "../lib/utils/randomUUID";
import { PERMISSION_DESCRIPTION, RULE_DESCRIPTION } from "../shared/constants/permission";
import {
  useAppConfigStore,
  useAppointmentStore,
  useAttendeesStore,
  useOutlookStore,
  useResourceStore,
} from "../shared/stores";
import { usePreSelectedResourceStore } from "../shared/stores/preSelectedResourceStore";
import { EPreSelectedResource } from "../shared/types/enums/preSelectedResourceEnum";
import { PreSelectedResourceInfo } from "../shared/types/preSelectedResource";

const lastSyncData: Map<string, string> = new Map();
lastSyncData.set("appointmentTime", "");
lastSyncData.set("recurringDates", "[]");
lastSyncData.set("startTime", "");

// Helper function để normalize keys của attendeeStatusResponse về lowercase
const normalizeAttendeeStatusResponse = (response: Record<string, IAttendeeInfo>): Record<string, IAttendeeInfo> => {
  const normalized: Record<string, IAttendeeInfo> = {};
  Object.keys(response).forEach((key: string) => {
    normalized[key.toLowerCase()] = response[key];
  });
  return normalized;
};

export const useTaskPane = () => {
  const queryClient = useQueryClient();
  const {
    selectedResource,
    setSelectedResource,
    meetingLabel: meetingLabelSelected,
    setMeetingLabel,
    setOriginalSelectedResources,
    originalSelectedResources,
    deskLocationList,
    setDeskLocationList,
    setIsCheckSyncResource,
  } = useResourceStore();
  const {
    requiredAttendees,
    optionalAttendees,
    enhancedLocation,
    startTime,
    endTime,
    startDateTimeZone,
    endDateTimeZone,
    timeZone,
    organizer,
    organizerName,
    iCalUId,
    customUid,
    recurringDates,
    isNoEndDateRecurring,
    globalId,
    guid,
    setGlobalId,
    setGuid,
  } = useOutlookStore();
  const { startDateTimeUtc, endDateTimeUtc } = useAppointmentStore();

  const { setItems } = usePreSelectedResourceStore();
  const { meetingLabel } = useAppConfigStore();

  const {
    requiredAttendees: requiredAttendeesStore,
    optionalAttendees: optionalAttendeesStore,
    locationUnknown,
    setRequiredAttendees,
    setOptionalAttendees,
    setLocationUnknown,
    setMainAttendee,
    removeRequiredAttendee,
    removeOptionalAttendee,
    setLoading
  } = useAttendeesStore();
  const { RemoveResources, getSessionId } = useOutlookActions();
  const startTimezoneString = useMemo(
    () =>
      `${startDateTimeZone?.year}-${((startDateTimeZone?.month ?? 0) + 1)
        .toString()
        .padStart(2, "0")}-${startDateTimeZone?.date.toString().padStart(2, "0")}`,
    [startDateTimeZone]
  );
  const endTimezoneString = useMemo(
    () =>
      `${endDateTimeZone?.year}-${((endDateTimeZone?.month ?? 0) + 1)
        .toString()
        .padStart(2, "0")}-${endDateTimeZone?.date.toString().padStart(2, "0")}`,
    [endDateTimeZone]
  );

  const splitNotify = (notifyInfos: PreSelectedResourceInfo[]) => {
    // Nhóm các item có cùng message lại với nhau
    const groupedByMessage = new Map<string, PreSelectedResourceInfo[]>();

    notifyInfos.forEach((item) => {
      // Sử dụng message làm key, nếu message null/undefined thì dùng empty string
      const messageKey = item.message ?? "";

      if (!groupedByMessage.has(messageKey)) {
        groupedByMessage.set(messageKey, []);
      }
      groupedByMessage.get(messageKey)!.push(item);
    });

    // Gom các item có cùng message thành 1 item mới với resources là tổng hợp
    const mergedItems: PreSelectedResourceInfo[] = [];

    groupedByMessage.forEach((items, messageKey) => {
      // Lấy item đầu tiên làm base
      const firstItem = items[0];

      // Gom tất cả resources từ các item có cùng message
      const allResources: ResourceEntity[] = [];
      items.forEach((item) => {
        allResources.push(...item.resources);
      });

      // Tạo item mới với resources đã được gom
      mergedItems.push(
        new PreSelectedResourceInfo({
          type: firstItem.type,
          message: firstItem.message,
          resources: allResources,
          forceOpenOrder: firstItem.forceOpenOrder,
          isResourcePending: firstItem.isResourcePending,
          resourcesAlternative: firstItem.resourcesAlternative,
        })
      );
    });

    setItems(mergedItems);
  };

  const showNotificationPreSelectedResource = async (response: GetSelectedResourceResponse, isRecurring: boolean) => {
    let notifyInfos: PreSelectedResourceInfo[] = [];
    let validResources = response.resources.filter((item) => !item.availability?.isCannotBook(isRecurring));
    let invalidResourcesByDeskLocation: ResourceEntity[] = [];
    const invalidResources = response.resources.filter((item) => item.availability?.isCannotBook(isRecurring));
    const validResourceClone = [...validResources];
    if (validResources.length > 0) {
      if (iCalUId && (!originalSelectedResources || originalSelectedResources.length === 0)) {
        const originalSelectedResourcesClone = validResources.map((item) => {
          if (response.haveOrderResources.includes(item.id)) {
            item.isOrdered = true;
          }
          return item;
        });
        setOriginalSelectedResources(originalSelectedResourcesClone);
      }
      for (let index = 0; index < validResourceClone.length; index++) {
        const item = validResourceClone[index];
        if (validResources.length > 1 && item.isDisableResourceByHotDeskRule(validResources, response.deskLocations)) {
          const permissionMessage = {
            type: EPreSelectedResource.Removed,
            message: PERMISSION_DESCRIPTION[EPermissionType.ExceedCapacity],
          };
          notifyInfos.push(
            new PreSelectedResourceInfo({
              type: permissionMessage.type,
              message: permissionMessage.message,
              resources: validResources,
            })
          );
          invalidResourcesByDeskLocation = [...invalidResourcesByDeskLocation, ...validResources];
          //RemoveResources(validResources);
          validResources = [];
          notifyInfos = notifyInfos.filter((item) => item.type === EPreSelectedResource.Removed);
          break; // Dừng vòng lặp ngay khi điều kiện đúng
        } else if (item.userRestriction && !response.haveOrderResources.includes(item.id)) {
          const sessionIdForOrderForm = await getSessionId(item.email?.toLowerCase());
          if (!sessionIdForOrderForm) {
            notifyInfos.push(
              new PreSelectedResourceInfo({
                type: EPreSelectedResource.OpenOderForm,
                message: item.userRestriction.message,
                resources: [item],
                forceOpenOrder: item.userRestriction.forceOpenOrder,
              })
            );
          }
        } else if (validResourceClone.length > 1 && item.isPooledResource) {
          validResources.splice(index, 1);
          invalidResources.push(item);
        }
      }
      if (validResources.length > 0) {
        if (
          meetingLabel &&
          meetingLabel.isRequired &&
          !response.bookedMeetingLabel &&
          (meetingLabelSelected === null || meetingLabelSelected === undefined)
        ) {
          notifyInfos.push(
            new PreSelectedResourceInfo({
              type: EPreSelectedResource.MeetingType,
              message: "",
              resources: validResources,
            })
          );
        }
      }
    }
    if (response.bookedMeetingLabel) {
      setMeetingLabel(response.bookedMeetingLabel.id);
    }

    // Chỉ set nếu có thay đổi - so sánh bằng ID
    const selectedResourceIds = new Set(selectedResource.map((r) => r.id).sort());
    const validResourceIds = new Set(validResources.map((r) => r.id).sort());
    const hasChanged =
      selectedResourceIds.size !== validResourceIds.size ||
      [...selectedResourceIds].some((id) => !validResourceIds.has(id)) ||
      [...validResourceIds].some((id) => !selectedResourceIds.has(id));
    if (hasChanged) {
      setSelectedResource(validResources);
    }

    if (invalidResources.length > 0 || invalidResourcesByDeskLocation.length > 0) {
      invalidResources.forEach((item) => {
        const permissionMessage = mapPermissionMessage(item, isRecurring);
        if (permissionMessage) {
          notifyInfos.push(
            new PreSelectedResourceInfo({
              type: permissionMessage.type,
              message: permissionMessage.message,
              resources: [item],
            })
          );
        } else if (item.isPooledResource) {
          notifyInfos.push(
            new PreSelectedResourceInfo({
              type: EPreSelectedResource.Removed,
              message: RULE_DESCRIPTION[EDisableRule.DisablePooledResource],
              resources: [item],
            })
          );
        }
      });
      await RemoveResources(invalidResources.concat(invalidResourcesByDeskLocation));
    }
    splitNotify(notifyInfos);
  };

  const saveOnlyMainAttendeeToStore = async (isRecurring: boolean) => {
    let mainAttendeeToSave: AttendeeInfo | null = null;
    if (organizer) {
      mainAttendeeToSave = new AttendeeInfo({
        userEmail: organizer,
        name: organizerName || organizer,
        isRequired: true,
        currentDate: startTimezoneString,
      });
    }

    const allAttendeeEmails = mainAttendeeToSave ? [mainAttendeeToSave.email] : [];

    const attendeeStatusRequest: IGetAttendeeStatusRequest = {
      userEmails: allAttendeeEmails,
      interval: isRecurring ? 1440 : 30,
      timeZoneStandardName: timeZone || "",
      timeZoneOccurrence: isRecurring ? "UTC" : timeZone || "",
      isRecurring: isRecurring,
      occurrences: isRecurring
        ? recurringDates
        : [
            {
              startDate: `${startTimezoneString}T00:00:00`,
              endDate: `${endTimezoneString}T23:59:59`,
            },
          ],
    };
    const attendeeStatusResponseRaw = await queryClient.fetchQuery(optionsAttendeeStatus(attendeeStatusRequest));
    const attendeeStatusResponse = normalizeAttendeeStatusResponse(attendeeStatusResponseRaw);
    if (mainAttendeeToSave) {
      const statusData = attendeeStatusResponse[mainAttendeeToSave.email.toLowerCase()];
      if (statusData) {
        mainAttendeeToSave.meetings =
          statusData.calendarEvents?.map(
            (event, index) => new AttendeeCalendarEvent(event, startTimezoneString, index)
          ) ?? [];
        mainAttendeeToSave.total = statusData.total;
        mainAttendeeToSave.noOfAvailable = statusData.noOfAvailable;
        mainAttendeeToSave.isBusy = statusData.isBusy;
        mainAttendeeToSave.isHoliday = statusData.isHoliday;
        mainAttendeeToSave.workingHours = statusData.workingHours;
        if (statusData.name) {
          mainAttendeeToSave.name = statusData.name;
        }
      } else {
        mainAttendeeToSave.isExternal = true;
      }
    }
    if (mainAttendeeToSave) {
      setMainAttendee(mainAttendeeToSave);
      setRequiredAttendees([]);
      setOptionalAttendees([]);
    }
  };

  const getAllAttendeeWhenDateChanged = async (responseResourceEmails: Set<string>, isRecurring: boolean) => {
    const requiredAttendeesToSave: AttendeeInfo[] = [];
    const optionalAttendeesToSave: AttendeeInfo[] = [];

    // Kiểm tra required attendees
    requiredAttendees.forEach((att) => {
      const email = att.Email.toLowerCase();
      // Là attendee nếu KHÔNG có trong response.resources
      if (!responseResourceEmails.has(email) && email !== organizer?.toLowerCase()) {
        requiredAttendeesToSave.push(
          new AttendeeInfo({
            userEmail: att.Email,
            name: att.Name || att.Email,
            isRequired: true,
            currentDate: startTimezoneString,
          })
        );
      }
    });

    // Tạo Set chứa email của required attendees để kiểm tra trùng lặp
    const requiredAttendeeEmails = new Set(requiredAttendeesToSave.map((att) => att.email.toLowerCase()));

    // Kiểm tra optional attendees
    optionalAttendees.forEach((att) => {
      const email = att.Email.toLowerCase();
      // Là attendee nếu KHÔNG có trong response.resources và KHÔNG có trong requiredAttendeesToSave
      if (!responseResourceEmails.has(email) && email !== organizer?.toLowerCase() && !requiredAttendeeEmails.has(email)) {
        optionalAttendeesToSave.push(
          new AttendeeInfo({
            userEmail: att.Email,
            name: att.Name || att.Email,
            isRequired: false,
            currentDate: startTimezoneString,
          })
        );
      }
    });

    // Tạo main attendee từ organizer
    let mainAttendeeToSave: AttendeeInfo | null = null;
    if (organizer) {
      mainAttendeeToSave = new AttendeeInfo({
        userEmail: organizer,
        name: organizerName || organizer,
        isRequired: true,
        currentDate: startTimezoneString,
      });
    }

    // Gọi API để lấy trạng thái attendees và organizer nếu có
    const allAttendeeEmails = [
      ...requiredAttendeesToSave.map((att) => att.email),
      ...optionalAttendeesToSave.map((att) => att.email),
      ...(mainAttendeeToSave ? [mainAttendeeToSave.email] : []),
    ];

    if (allAttendeeEmails.length > 0) {
      const attendeeStatusRequest: IGetAttendeeStatusRequest = {
        userEmails: [...new Set(allAttendeeEmails)],
        interval: isRecurring ? 1440 : 30,
        timeZoneStandardName: timeZone || "",
        timeZoneOccurrence: isRecurring ? "UTC" : timeZone || "",
        isRecurring: isRecurring,
        occurrences: isRecurring
          ? recurringDates
          : [
              {
                startDate: `${startTimezoneString}T00:00:00`,
                endDate: `${endTimezoneString}T23:59:59`,
              },
            ],
      };

      setLoading(true);
      const attendeeStatusResponseRaw = await queryClient.fetchQuery(optionsAttendeeStatus(attendeeStatusRequest));
      const attendeeStatusResponse = normalizeAttendeeStatusResponse(attendeeStatusResponseRaw);
      setLoading(false);
      if (lastSyncData.get("startTime") !== startTimezoneString) {
        lastSyncData.set("startTime", startTimezoneString);
      }
      // Cập nhật thông tin main attendee (organizer) từ API response
      if (mainAttendeeToSave) {
        const statusData = attendeeStatusResponse[mainAttendeeToSave.email.toLowerCase()];
        if (statusData) {
          mainAttendeeToSave.meetings =
            statusData.calendarEvents?.map(
              (event, index) => new AttendeeCalendarEvent(event, startTimezoneString, index)
            ) ?? [];
          mainAttendeeToSave.total = statusData.total;
          mainAttendeeToSave.noOfAvailable = statusData.noOfAvailable;
          mainAttendeeToSave.isBusy = statusData.isBusy;
          mainAttendeeToSave.isHoliday = statusData.isHoliday;
          mainAttendeeToSave.workingHours = statusData.workingHours;
          if (statusData.name) {
            mainAttendeeToSave.name = statusData.name;
          }
        } else {
          mainAttendeeToSave.isExternal = true;
        }
      }

      // Cập nhật thông tin attendees từ API response
      requiredAttendeesToSave.forEach((attendee) => {
        const statusData = attendeeStatusResponse[attendee.email.toLowerCase()];
        if (statusData) {
          attendee.meetings =
            statusData.calendarEvents?.map(
              (event, index) => new AttendeeCalendarEvent(event, startTimezoneString, index)
            ) ?? [];
          attendee.total = statusData.total;
          attendee.noOfAvailable = statusData.noOfAvailable;
          attendee.isBusy = statusData.isBusy;
          attendee.isHoliday = statusData.isHoliday;
          attendee.workingHours = statusData.workingHours;
          if (statusData.name) {
            attendee.name = statusData.name;
          }
        } else {
          attendee.isExternal = true;
        }
      });

      optionalAttendeesToSave.forEach((attendee) => {
        const statusData = attendeeStatusResponse[attendee.email.toLowerCase()];
        if (statusData) {
          attendee.meetings =
            statusData.calendarEvents?.map(
              (event, index) => new AttendeeCalendarEvent(event, startTimezoneString, index)
            ) ?? [];
          attendee.total = statusData.total;
          attendee.noOfAvailable = statusData.noOfAvailable;
          attendee.isBusy = statusData.isBusy;
          attendee.isHoliday = statusData.isHoliday;
          attendee.workingHours = statusData.workingHours;
          if (statusData.name) {
            attendee.name = statusData.name;
          }
        } else {
          attendee.isExternal = true;
        }
      });
    }

    // Lưu main attendee (organizer) vào store sau khi đã cập nhật thông tin từ API
    if (mainAttendeeToSave) {
      setMainAttendee(mainAttendeeToSave);
    }

    // Lưu attendees vào store sau khi đã cập nhật thông tin từ API
    if (requiredAttendeesToSave.length > 0) {
      setRequiredAttendees(requiredAttendeesToSave);
    }
    if (optionalAttendeesToSave.length > 0) {
      setOptionalAttendees(optionalAttendeesToSave);
    }
  };

  const updateAttendeesWhenDateUnchanged = async (responseResourceEmails: Set<string>, isRecurring: boolean) => {
    // Tìm attendees mới (chưa có trong store) - chỉ gọi API cho những cái này
    const newRequiredAttendees: AttendeeInfo[] = [];
    const newOptionalAttendees: AttendeeInfo[] = [];
    const existingRequiredEmails = new Set(requiredAttendeesStore.map((att) => att.email.toLowerCase()));
    const existingOptionalEmails = new Set(optionalAttendeesStore.map((att) => att.email.toLowerCase()));

    // Tìm required attendees mới
    requiredAttendees.forEach((att) => {
      const email = att.Email.toLowerCase();
      if (
        !responseResourceEmails.has(email) &&
        email !== organizer?.toLowerCase() &&
        !existingRequiredEmails.has(email)
      ) {
        newRequiredAttendees.push(
          new AttendeeInfo({
            userEmail: att.Email,
            name: att.Name || att.Email,
            isRequired: true,
            currentDate: startTimezoneString,
          })
        );
      }
    });

    // Tạo Set chứa email của required attendees mới để kiểm tra trùng lặp
    const newRequiredAttendeeEmails = new Set(requiredAttendees.map((att) => att.Email.toLowerCase()));

    // Tìm optional attendees mới
    optionalAttendees.forEach((att) => {
      const email = att.Email.toLowerCase();
      if (
        !responseResourceEmails.has(email) &&
        email !== organizer?.toLowerCase() &&
        !existingOptionalEmails.has(email) &&
        !newRequiredAttendeeEmails.has(email)
      ) {
        newOptionalAttendees.push(
          new AttendeeInfo({
            userEmail: att.Email,
            name: att.Name || att.Email,
            isRequired: false,
            currentDate: startTimezoneString,
          })
        );
      }
    });

    // Xóa attendees không còn trong Outlook khỏi store
    const currentOutlookEmails = new Set<string>();
    requiredAttendees.forEach((att) => {
      const email = att.Email.toLowerCase();
      if (!responseResourceEmails.has(email) && email !== organizer?.toLowerCase()) {
        currentOutlookEmails.add(email);
      }
    });
    optionalAttendees.forEach((att) => {
      const email = att.Email.toLowerCase();
      if (!responseResourceEmails.has(email) && email !== organizer?.toLowerCase()) {
        currentOutlookEmails.add(email);
      }
    });

    // Xóa required attendees không còn trong Outlook và tính toán lại danh sách
    const remainingRequiredAttendees = requiredAttendeesStore.filter((att) => {
      if (!currentOutlookEmails.has(att.email.toLowerCase())) {
        removeRequiredAttendee(att.email);
        return false;
      }
      return true;
    });

    // Xóa optional attendees không còn trong Outlook và tính toán lại danh sách
    const remainingOptionalAttendees = optionalAttendeesStore.filter((att) => {
      if (!currentOutlookEmails.has(att.email.toLowerCase())) {
        removeOptionalAttendee(att.email);
        return false;
      }
      return true;
    });

    // Gọi API chỉ cho attendees mới (main attendee không cần gọi vì đã được gọi khi thay đổi ngày)
    const allNewAttendeeEmails = [
      ...newRequiredAttendees.map((att) => att.email),
      ...newOptionalAttendees.map((att) => att.email),
    ];

    if (allNewAttendeeEmails.length > 0) {
      const attendeeStatusRequest: IGetAttendeeStatusRequest = {
        userEmails: allNewAttendeeEmails,
        interval: isRecurring ? 1440 : 30,
        timeZoneStandardName: timeZone || "",
        timeZoneOccurrence: isRecurring ? "UTC" : timeZone || "",
        isRecurring: isRecurring,
        occurrences: isRecurring
          ? recurringDates
          : [
              {
                startDate: `${startTimezoneString}T00:00:00`,
                endDate: `${endTimezoneString}T23:59:59`,
              },
            ],
      };

      setLoading(true);
      const attendeeStatusResponseRaw = await queryClient.fetchQuery(optionsAttendeeStatus(attendeeStatusRequest));
      const attendeeStatusResponse = normalizeAttendeeStatusResponse(attendeeStatusResponseRaw);
      setLoading(false);

      // Cập nhật thông tin required attendees mới
      newRequiredAttendees.forEach((attendee) => {
        const statusData = attendeeStatusResponse[attendee.email.toLowerCase()];
        if (statusData) {
          attendee.meetings =
            statusData.calendarEvents?.map(
              (event, index) => new AttendeeCalendarEvent(event, startTimezoneString, index)
            ) ?? [];
          attendee.total = statusData.total;
          attendee.noOfAvailable = statusData.noOfAvailable;
          attendee.isBusy = statusData.isBusy;
          attendee.isHoliday = statusData.isHoliday;
          attendee.workingHours = statusData.workingHours;
          if (statusData.name) {
            attendee.name = statusData.name;
          }
        } else {
          attendee.isExternal = true;
        }
      });

      // Cập nhật thông tin optional attendees mới
      newOptionalAttendees.forEach((attendee) => {
        const statusData = attendeeStatusResponse[attendee.email.toLowerCase()];
        if (statusData) {
          attendee.meetings =
            statusData.calendarEvents?.map(
              (event, index) => new AttendeeCalendarEvent(event, startTimezoneString, index)
            ) ?? [];
          attendee.total = statusData.total;
          attendee.noOfAvailable = statusData.noOfAvailable;
          attendee.isBusy = statusData.isBusy;
          attendee.isHoliday = statusData.isHoliday;
          attendee.workingHours = statusData.workingHours;
          if (statusData.name) {
            attendee.name = statusData.name;
          }
        } else {
          attendee.isExternal = true;
        }
      });

      // Thêm attendees mới vào store (sau khi đã xóa những cái không còn trong Outlook)
      if (newRequiredAttendees.length > 0) {
        setRequiredAttendees([...remainingRequiredAttendees, ...newRequiredAttendees]);
      }
      if (newOptionalAttendees.length > 0) {
        setOptionalAttendees([...remainingOptionalAttendees, ...newOptionalAttendees]);
      }
    }
  };

  const saveDataToStore = async (response: GetSelectedResourceResponse | null, isRecurring: boolean) => {
    if (!response) {
      await saveOnlyMainAttendeeToStore(isRecurring);
    } else {
      if (iCalUId && globalId !== response.globalId) {
        setGlobalId(response.globalId);
      }
      if (guid !== response.guid) {
        setGuid(response.guid);
      }
      // Tạo Set chứa email của resources và childResources
      const responseResourceEmails = new Set<string>();
      response.resources.forEach((r) => {
        if (r.email) {
          responseResourceEmails.add(r.email.toLowerCase());
        }
        // Thêm email của childResources
        if (r.childResources && r.childResources.length > 0) {
          r.childResources.forEach((childResource) => {
            if (childResource.email) {
              responseResourceEmails.add(childResource.email.toLowerCase());
            }
          });
        }
      });

      // Phân loại attendees: Là attendee nếu có trong Outlook attendees nhưng KHÔNG có trong response.resources
      if (
        startTimezoneString !== lastSyncData.get("startTime") ||
        JSON.stringify(recurringDates) !== lastSyncData.get("recurringDates")
      ) {
        await getAllAttendeeWhenDateChanged(responseResourceEmails, isRecurring);
      } else {
        await updateAttendeesWhenDateUnchanged(responseResourceEmails, isRecurring);
      }

      if (lastSyncData.get("recurringDates") !== JSON.stringify(recurringDates)) {
        lastSyncData.set("recurringDates", JSON.stringify(recurringDates));
      }

      // ✅ Kiểm tra và lưu locationUnknown từ enhancedLocation
      // Location unknown là location có trong enhancedLocation nhưng KHÔNG có trong response.resources
      const locationUnknownToSave: string[] = [];

      enhancedLocation.forEach((location) => {
        // Get email from emailAddress or locationIdentifier.id (consistent with GetRecipients logic)
        const locationEmail = location.emailAddress || location.displayName || location.locationIdentifier?.id || "";
        const locationEmailLower = locationEmail.toLowerCase();

        // Là location unknown nếu có email và KHÔNG có trong response.resources
        if (locationEmail && !responseResourceEmails.has(locationEmailLower)) {
          locationUnknownToSave.push(locationEmail.toLowerCase());
        }
      });

      // Lưu locationUnknown vào store (luôn set, kể cả khi rỗng để clear previous values)
      setLocationUnknown(locationUnknownToSave);
      //resource data
      showNotificationPreSelectedResource(response, isRecurring);
    }
  };

  const callGetResourceBookingForMeetingLabel = async () => {
    const request: IGetResourceBookingRequest = {
      requestId: generateCId(),
      organizer: organizer,
      timeZoneStandardName: timeZone || "",
      attendees: [],
      uid: customUid || "",
      globalId: globalId || "",
      isNoEndDate: recurringDates?.length > 0 ? isNoEndDateRecurring : null,
      isRecurring: recurringDates?.length > 0,
      occurrences:
        recurringDates?.length > 0
          ? recurringDates.map((date) => ({
              utcStartDate: date.startDate || "",
              utcEndDate: date.endDate || "",
            }))
          : [
              {
                utcStartDate: startDateTimeUtc || startTime?.toISOString() || "",
                utcEndDate: endDateTimeUtc || endTime?.toISOString() || "",
              },
            ],
    };
    const response = await queryClient.fetchQuery(options(request));
    if (response.bookedMeetingLabel?.id) {
      setMeetingLabel(response.bookedMeetingLabel.id);
    }
    if (!deskLocationList?.length) {
      setDeskLocationList(response.deskLocations);
    }
  };

  const syncAllResources = async () => {
    try {
      setIsCheckSyncResource(true);
      const enhancedLocationData = enhancedLocation.map((location) =>
        (location.emailAddress || location.displayName).toLowerCase()
      );
      const requiredData = requiredAttendees
        .filter((attendee) => !enhancedLocationData.includes(attendee.Email.toLowerCase()))
        .map((attendee) => attendee.Email.toLowerCase());

      const optionalData = optionalAttendees
        .filter((attendee) => !enhancedLocationData.includes(attendee.Email.toLowerCase()))
        .map((attendee) => attendee.Email.toLowerCase());
     
      const appointmentTimeData = JSON.stringify({ startTime, endTime, startDateTimeZone, endDateTimeZone, timeZone });
      const recurringDatesData = JSON.stringify(recurringDates);

      const selectedResourceData = [
        ...new Set(
          selectedResource
            .flatMap((rs) => {
              // Nếu có childResources, chỉ lấy email từ childResources, không lấy resource cha
              if (rs.childResources && rs.childResources.length > 0) {
                return rs.childResources.map((child) => child.email.toLowerCase()).filter(Boolean);
              }
              // Nếu không có childResources, lấy email của resource cha
              return rs.email ? [rs.email.toLowerCase()] : [];
            })
            .concat(locationUnknown || [])
        ),
      ];

      // Gộp các mảng để so sánh chung
      const currentDataArray = [
        ...new Set([...requiredData, ...optionalData, ...enhancedLocationData]),
      ].sort();
      
      const storeDataArray = [
        ...new Set([
          ...requiredAttendeesStore.map((attendee) => attendee.email.toLowerCase()),
          ...optionalAttendeesStore.map((attendee) => attendee.email.toLowerCase()),
          ...selectedResourceData,
        ]),
      ].sort();

      const dataChanged =
        JSON.stringify(currentDataArray) !== JSON.stringify(storeDataArray) ||
        appointmentTimeData !== lastSyncData.get("appointmentTime") ||
        recurringDatesData !== lastSyncData.get("recurringDates");

      const allAttendeeEmails = [...currentDataArray].filter(Boolean);

      if (meetingLabel && meetingLabel.isRequired && allAttendeeEmails.length === 0 && !meetingLabelSelected) {
        await callGetResourceBookingForMeetingLabel();
        await saveDataToStore(null, recurringDates?.length > 0);
        if (lastSyncData.get("appointmentTime") !== appointmentTimeData) {
          lastSyncData.set("appointmentTime", appointmentTimeData);
        }
        if (lastSyncData.get("recurringDates") !== JSON.stringify(recurringDates)) {
          lastSyncData.set("recurringDates", JSON.stringify(recurringDates));
        }
        return;
      }

      if (
        !dataChanged ||
        (!allAttendeeEmails.length && !organizer) ||
        !organizer ||
        !startTime ||
        !endTime ||
        !timeZone ||
        !meetingLabel
      ) {
        if (allAttendeeEmails.length === 0) {
          if (lastSyncData.get("appointmentTime") !== appointmentTimeData) {
            lastSyncData.set("appointmentTime", appointmentTimeData);
          }
          if (lastSyncData.get("recurringDates") !== JSON.stringify(recurringDates)) {
            lastSyncData.set("recurringDates", JSON.stringify(recurringDates));
          }
          setSelectedResource([]);
        }
        return;
      }
      const request: IGetResourceBookingRequest = {
        requestId: generateCId(),
        organizer: organizer,
        timeZoneStandardName: timeZone || "",
        attendees: [...new Set(allAttendeeEmails)],
        uid: customUid || "",
        globalId: globalId || "",
        isNoEndDate: recurringDates?.length > 0 ? isNoEndDateRecurring : null,
        isRecurring: recurringDates?.length > 0,
        occurrences:
          recurringDates?.length > 0
            ? recurringDates.map((date) => ({
                utcStartDate: date.startDate || "",
                utcEndDate: date.endDate || "",
              }))
            : [
                {
                  utcStartDate: startDateTimeUtc || startTime.toISOString(),
                  utcEndDate: endDateTimeUtc || endTime.toISOString(),
                },
              ],
      };
      if (lastSyncData.get("appointmentTime") !== appointmentTimeData) {
        lastSyncData.set("appointmentTime", appointmentTimeData);
      }
      if (request.attendees.length) {
        const response = await queryClient.fetchQuery(options(request));
        if (!deskLocationList?.length) {
          setDeskLocationList(response.deskLocations);
        }
        await saveDataToStore(response, request.isRecurring);
      } else {
        setSelectedResource([]);
        await saveDataToStore(null, request.isRecurring);
      }
    } catch (error) {
      console.error("Error syncing all resources:", error);
    } finally {
      setIsCheckSyncResource(false);
    }
  };

  return {
    syncAllResources,
  };
};
